import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ClientLayout } from "@/components/layout/ClientLayout";
import { api, formatUSD } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { CheckCircle, X, Spinner } from "@phosphor-icons/react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("polling");
  const [info, setInfo] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") {
          await refresh();
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= 8) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        attemptsRef.current += 1;
        if (attemptsRef.current >= 8) setStatus("error");
        else setTimeout(poll, 2000);
      }
    };
    poll();
    return () => {
      stopped = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <ClientLayout>
      <div className="max-w-xl mx-auto pt-12 text-center" data-testid="payment-success-page">
        {status === "polling" && (
          <>
            <Spinner size={48} className="mx-auto text-[#FF2D2D] animate-spin" />
            <h1 className="font-display text-4xl mt-4">Confirming payment…</h1>
            <p className="text-[#a3a3a3] mt-2">Hold tight, this takes a moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle weight="fill" size={64} className="mx-auto text-[#FF2D2D]" />
            <h1 className="font-display text-5xl mt-4">Payment received.</h1>
            <p className="text-[#a3a3a3] mt-2">
              {info?.type === "points_package" ? "Points have been added to your balance." : "Project payment recorded."}
            </p>
            <div className="mt-8 flex justify-center gap-3">
              {info?.type?.startsWith("project") && (
                <Link to={`/projects/${info.reference_id}`} className="btn-outline px-5 py-3 text-sm uppercase tracking-wider" data-testid="goto-project">
                  Go to project
                </Link>
              )}
              <Link to="/dashboard" className="btn-primary px-5 py-3 text-sm font-bold uppercase tracking-wider" data-testid="goto-dashboard">
                Back to dashboard
              </Link>
            </div>
          </>
        )}
        {(status === "expired" || status === "error" || status === "timeout") && (
          <>
            <X weight="bold" size={48} className="mx-auto text-[#ff2a00]" />
            <h1 className="font-display text-4xl mt-4">Payment unconfirmed</h1>
            <p className="text-[#a3a3a3] mt-2">Please check your dashboard or try again.</p>
            <button onClick={() => navigate("/dashboard")} className="btn-outline mt-6 px-5 py-3 text-sm uppercase tracking-wider">
              Back to dashboard
            </button>
          </>
        )}
      </div>
    </ClientLayout>
  );
}
